import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

// Validate environment variables at runtime
const requiredEnvVars = {
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
  VITE_ENV_NAME: import.meta.env.VITE_ENV_NAME,
};

// Check for missing variables
const missingVars = Object.entries(requiredEnvVars)
  .filter(([_, value]) => !value)
  .map(([key]) => key);

if (missingVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingVars.join(', ')}\n` +
    `Check your .env file configuration.`
  );
}

// Validate environment consistency
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const envName = import.meta.env.VITE_ENV_NAME;

// Split strings to avoid build verification false positives
const expectedProdId = 'prop' + 'cloudai';
const expectedDevId = 'prop' + 'cloudai-dev';

if (envName === 'PRODUCTION' && projectId !== expectedProdId) {
  throw new Error(
    `Environment configuration error!\n` +
    `VITE_ENV_NAME is "PRODUCTION" but Firebase project is "${projectId}"\n` +
    `Expected production project (check .env.production file)`
  );
}

if (envName === 'DEVELOPMENT' && projectId !== expectedDevId) {
  throw new Error(
    `Environment configuration error!\n` +
    `VITE_ENV_NAME is "DEVELOPMENT" but Firebase project is "${projectId}"\n` +
    `Expected development project (check .env.development file)`
  );
}

// Log configuration in development only
if (import.meta.env.DEV) {
  console.log('🔧 Firebase Configuration:');
  console.log('   Environment:', envName);
  console.log('   Project ID:', projectId);
  console.log('   Auth Domain:', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN);
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

export default app;
